import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useUserRole } from '../hooks/useUserRole';
import './UnauthorizedPage.css';

function UnauthorizedPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, logout, organization, hasPendingOrganization, checkUser } = useAuth();
  const { role, loading, canAccessAdminPanel, roleWasDefaulted } = useUserRole();
  const [recovering, setRecovering] = useState(false);
  const [recoveryError, setRecoveryError] = useState(null);

  useEffect(() => {
    if (!loading && user && canAccessAdminPanel()) {
      navigate('/', { replace: true });
    }
  }, [loading, user, role, canAccessAdminPanel, navigate]);

  // Caso: cuenta sin rol asignado todavia (ej. registro de owner que quedo
  // a mitad de camino) Y con datos de registro pendientes en este mismo
  // navegador. En vez de dejar al usuario en un callejon sin salida, le
  // ofrecemos completar el registro (crea organizacion+restaurante) en
  // lugar de solo Logout / Volver al login.
  const canRecoverRegistration =
    !loading && user && roleWasDefaulted && !organization && hasPendingOrganization();

  const handleCompleteRegistration = async () => {
    setRecovering(true);
    setRecoveryError(null);
    try {
      await checkUser(true);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error completando el registro:', error);
      setRecoveryError(t('unauthorized.recoveryError'));
      setRecovering(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
      navigate('/login', { replace: true });
    }
  };

  const handleGoBack = () => {
    navigate('/login', { replace: true });
  };

  const getRoleMessage = () => {
    // Si el rol vino del fallback por defecto (no hay grupo ni custom:role
    // real), no es una cuenta Comanda genuina: no mostramos ese mensaje.
    switch (!roleWasDefaulted && role) {
      case 'COMANDA':
        return t('unauthorized.roleComanda');
      default:
        return t('unauthorized.roleDefault');
    }
  };

  if (loading) {
    return null;
  }

  return (
    <div className="unauthorized-page">
      <div className="unauthorized-container">
        <div className="unauthorized-icon">🚫</div>
        <h1>{t('unauthorized.title')}</h1>
        <p className="unauthorized-message">{t('unauthorized.message')}</p>
        {user && <p className="role-info">{getRoleMessage()}</p>}

        {role === 'COMANDA' && !roleWasDefaulted && (
          <div className="comanda-info">
            <p>
              {t('unauthorized.comandaRedirect')} <strong>{t('unauthorized.comandaApp')}</strong>
            </p>
            <p className="app-url">
              <a
                href={process.env.REACT_APP_COMANDA_URL || '/'}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('unauthorized.goToComanda')}
              </a>
            </p>
          </div>
        )}

        {canRecoverRegistration && (
          <div className="recovery-info">
            <p>{t('unauthorized.recoveryMessage')}</p>
            {recoveryError && <p className="recovery-error">{recoveryError}</p>}
          </div>
        )}

        <div className="unauthorized-actions">
          {canRecoverRegistration && (
            <button
              type="button"
              onClick={handleCompleteRegistration}
              className="btn btn-primary"
              disabled={recovering}
            >
              {recovering ? t('unauthorized.recovering') : t('unauthorized.completeRegistration')}
            </button>
          )}
          <button
            type="button"
            onClick={handleLogout}
            className={canRecoverRegistration ? 'btn btn-secondary' : 'btn btn-primary'}
          >
            {t('unauthorized.logout')}
          </button>
          <button type="button" onClick={handleGoBack} className="btn btn-secondary">
            {t('unauthorized.goToLogin')}
          </button>
        </div>

        <div className="contact-info">
          <p>{t('unauthorized.contactAdmin')}</p>
        </div>
      </div>
    </div>
  );
}

export default UnauthorizedPage;
